"use client";

import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { 
  MessageSquare, 
  Plus, 
  Search, 
  LifeBuoy, 
  Clock, 
  CheckCircle2, 
  AlertCircle,
  FileText,
  ChevronRight,
  MoreVertical,
  Paperclip,
  Send
} from "lucide-react";
import { cn } from "@/lib/utils";
import Link from "next/link";

const tickets = [
  {
    id: "TKT-10293",
    subject: "Missing payout for transaction TRX-9928",
    category: "Payouts",
    status: "Open",
    priority: "High",
    lastUpdate: "2h ago",
    date: "Oct 24, 2023"
  },
  {
    id: "TKT-10285",
    subject: "API Key integration error on production",
    category: "Technical",
    status: "Pending",
    priority: "Medium",
    lastUpdate: "1d ago",
    date: "Oct 23, 2023"
  },
  {
    id: "TKT-10250",
    subject: "Account limit increase request",
    category: "Account",
    status: "Resolved",
    priority: "Low",
    lastUpdate: "3d ago",
    date: "Oct 21, 2023"
  }
];

export default function SupportPage() {
  const [isCreating, setIsCreating] = useState(false);

  if (isCreating) {
    return (
      <div className="space-y-6 w-full animate-in fade-in duration-500">
        <div className="flex flex-col gap-1">
          <button 
            onClick={() => setIsCreating(false)}
            className="flex items-center text-[10px] font-bold text-muted-foreground hover:text-foreground uppercase tracking-wider transition-colors w-fit"
          >
            <ChevronRight className="h-3 w-3 rotate-180 mr-1" />
            Back to Support
          </button>
          <h1 className="text-xl font-bold tracking-tight text-foreground">New Support Ticket</h1>
        </div>

        <Card className="border shadow-sm">
          <CardHeader className="py-4 px-5 border-b bg-muted/5">
            <CardTitle className="text-sm font-bold">Ticket Details</CardTitle>
            <CardDescription className="text-[10px]">Describe your issue for our support team.</CardDescription>
          </CardHeader>
          <CardContent className="p-5 space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="category" className="text-[10px] font-bold uppercase text-muted-foreground">Category</Label>
                <select id="category" className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-xs focus:ring-1 focus:ring-primary">
                  <option>Select Category...</option>
                  <option>Settlements & Payouts</option>
                  <option>Technical / API</option>
                  <option>Account & KYC</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="priority" className="text-[10px] font-bold uppercase text-muted-foreground">Priority</Label>
                <select id="priority" className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-xs focus:ring-1 focus:ring-primary">
                  <option>Medium</option>
                  <option>Low</option>
                  <option>High</option>
                </select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="subject" className="text-[10px] font-bold uppercase text-muted-foreground">Subject</Label>
              <Input id="subject" placeholder="Summary of issue" className="h-9 text-xs" />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="description" className="text-[10px] font-bold uppercase text-muted-foreground">Description</Label>
              <textarea 
                id="description" 
                className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-xs focus:ring-1 focus:ring-primary resize-none"
                placeholder="Provide details..."
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-[10px] font-bold uppercase text-muted-foreground">Attachments</Label>
              <div className="border border-dashed rounded-lg p-6 flex flex-col items-center justify-center text-center bg-muted/5 hover:bg-muted/10 transition-colors cursor-pointer">
                <Paperclip className="h-4 w-4 text-muted-foreground mb-1.5" />
                <p className="text-[11px] font-semibold">Upload Files</p>
                <p className="text-[9px] text-muted-foreground mt-0.5">Max 10MB per file</p>
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex justify-end gap-2 border-t p-4 bg-muted/5">
            <Button variant="ghost" size="sm" className="h-8 text-[11px] font-bold uppercase" onClick={() => setIsCreating(false)}>Cancel</Button>
            <Button size="sm" className="bg-primary text-primary-foreground h-8 px-4 text-[11px] font-bold uppercase" onClick={() => {
              alert("Ticket submitted.");
              setIsCreating(false);
            }}>
              Submit Ticket
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Support Hub</h1>
          <p className="text-sm text-muted-foreground">Assistance for your account and operations.</p>
        </div>
        <Button size="sm" className="bg-primary text-primary-foreground h-9 px-4 rounded-lg font-semibold shadow-sm" onClick={() => setIsCreating(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Create Ticket
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[240px_1fr]">
        <aside className="space-y-4">
          <Card className="border shadow-sm">
            <CardHeader className="py-3 px-4 border-b bg-muted/5">
              <CardTitle className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Quick Help</CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              {[
                { t: "Live Support", d: "Available 24/7", i: LifeBuoy, c: "text-primary bg-primary/5" },
                { t: "WhatsApp", d: "+234 901...", i: MessageSquare, c: "text-emerald-600 bg-emerald-50" },
              ].map((item) => (
                <div key={item.t} className="flex items-center gap-3">
                  <div className={cn("h-7 w-7 rounded-lg flex items-center justify-center", item.c)}>
                    <item.i className="h-3.5 w-3.5" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold">{item.t}</p>
                    <p className="text-[10px] text-muted-foreground">{item.d}</p>
                  </div>
                </div>
              ))}
              
              <div className="pt-3 border-t">
                <div className="p-2.5 rounded-lg bg-muted/30 border space-y-1.5">
                  <div className="flex items-center gap-2 text-primary">
                    <Clock className="h-3 w-3" />
                    <span className="text-[9px] font-bold uppercase tracking-wider">SLA Status</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground leading-relaxed">
                    Avg response: <strong>45 mins</strong>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </aside>

        <section>
          <Card className="border shadow-sm">
            <CardHeader className="py-3 px-4 border-b bg-muted/5 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-bold">Ticket History</CardTitle>
              <div className="relative w-40">
                <Search className="absolute left-2.5 top-2.5 h-3 w-3 text-muted-foreground" />
                <Input placeholder="Search..." className="h-7 pl-8 text-[10px] rounded-md" />
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader className="bg-muted/10">
                  <TableRow>
                    <TableHead className="px-5 h-9 text-[10px] font-bold uppercase tracking-wider">ID</TableHead>
                    <TableHead className="h-9 text-[10px] font-bold uppercase tracking-wider">Subject</TableHead>
                    <TableHead className="h-9 text-[10px] font-bold uppercase tracking-wider">Status</TableHead>
                    <TableHead className="h-9 text-[10px] font-bold uppercase tracking-wider">Updated</TableHead>
                    <TableHead className="h-9 text-right px-5"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tickets.map((ticket) => (
                    <TableRow key={ticket.id} className="hover:bg-muted/10 border-b last:border-0 transition-all">
                      <TableCell className="px-5 font-mono text-[9px] text-muted-foreground">
                        {ticket.id}
                      </TableCell>
                      <TableCell>
                        <div className="text-xs font-semibold">{ticket.subject}</div>
                        <div className="text-[9px] text-muted-foreground font-medium">{ticket.category} • {ticket.priority}</div>
                      </TableCell>
                      <TableCell>
                        <Badge className={cn(
                          "text-[8px] h-4 px-1.5 font-bold uppercase border-none",
                          ticket.status === "Open" ? "bg-emerald-100 text-emerald-700" :
                          ticket.status === "Pending" ? "bg-amber-100 text-amber-700" :
                          "bg-muted text-muted-foreground"
                        )}>
                          {ticket.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-[10px] text-muted-foreground">{ticket.lastUpdate}</TableCell>
                      <TableCell className="text-right px-5">
                        <Link href={`/dashboard/support/${ticket.id}`}>
                          <Button variant="ghost" size="icon" className="h-7 w-7 rounded-md hover:bg-primary/5 transition-all">
                            <ChevronRight className="h-3.5 w-3.5" />
                          </Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
}
